import { useContext, useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { Button, Dropdown, Form, FormProps, Input, Modal, Segment, Select, Table, TextArea } from "semantic-ui-react";
import { dialogControl } from "../controls/dialog-control";
import { useCurrentUser } from "../hooks/auth-hook";
import { ModalControlData, useModalControl } from "../hooks/commonHooks";
import { useFormConnect, useFormData } from "../hooks/formHooks";
import { restRequest } from "../http";
import { FileContainerType, PageData } from "../models/common_model";
import { EquipmentCalibrationData } from "../models/equipment_model";
import { fieldError, formatDate, formatDateTime } from "../utils";
import { Page } from "./commons/Page";
import { LewittRemoteSearchDropdown } from "./commons/LewittDropdown";
import { FilesInput } from "./commons/FilesInput";
import { LanguageContext } from "../context";

export function EquipmentCalibrationComponent(props: { equipmentId: number, modalControl: ModalControlData }) {
    const { equipmentId, modalControl } = props;
    const currentUser = useCurrentUser();
    const [searchData, setSearchField] = useFormData<any>({});
    const [calibrations, setSearchData, searchLoading] = useFormConnect<any, PageData<EquipmentCalibrationData>>({}, `equipments/${equipmentId}/calibrations/search`);

    const editModalControl = useModalControl();

    const [currentEditId, setCurrentEditId] = useState<number>();

    const [t, lang] = useContext(LanguageContext);


    return <>
        <Modal {...modalControl.props} size="large">
            <Modal.Header>{t('Inventory')} #{equipmentId} {t('Calibrations')}</Modal.Header>
            <Modal.Content>
                <Segment.Group>
                    <Segment attached="top" loading={searchLoading}>
                        <Form onSubmit={e => setSearchData(searchData)}>
                            <Form.Group>
                                <Form.Input inline className="date" label={t("Calibration Date")}>
                                    <table cellPadding={0} cellSpacing={0}>
                                        <tr>
                                            <td>
                                                <ReactDatePicker
                                                    placeholderText={t("From")}
                                                    dateFormat="yyyy-MM-d"
                                                    selected={searchData.start_date} onChange={(value: Date) => setSearchField(null, { name: 'start_date', value })}></ReactDatePicker>
                                            </td>
                                            <th style={{ padding: '0 3px' }}> - </th>
                                            <td>
                                                <ReactDatePicker
                                                    placeholderText={t("To")}
                                                    dateFormat="yyyy-MM-d"
                                                    minDate={searchData.start_date}
                                                    selected={searchData.end_date} onChange={(value: Date) => setSearchField(null, { name: 'end_date', value })}></ReactDatePicker>
                                            </td>
                                        </tr>
                                    </table>
                                </Form.Input>

                                <div><Button icon="search" type="submit"></Button></div>
                            </Form.Group>
                        </Form>
                    </Segment>
                    <Segment attached="bottom">
                        <Table striped>
                            <Table.Header>
                                <Table.Row>
                                    <th>ID</th>
                                    <th>{t('Calibration Date')}</th>
                                    <th>{t('Next Calibration')}</th>
                                    <th>{t('Reason For Missing Calibration')}</th>

                                    <th>{t('Created By')}</th>
                                    <th>{t('Created At')}</th>
                                    <th>{currentUser.permissions.equipment.calibration.create && <Button color="blue" icon="add" content={t("Calibration")} size="mini" onClick={e => {
                                        setCurrentEditId(undefined);
                                        editModalControl.setOpen(true);
                                    }} />}</th>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {calibrations?.items.map(calibration => {
                                    return <tr key={calibration.id}>
                                        <td>{calibration.id}</td>
                                        <td>{formatDate(calibration.calibration_date)}</td>
                                        <td>{formatDate(calibration.next_calibration_date)}</td>
                                        <td>{calibration.cause_of_no_calibration}</td>
                                        <td>{calibration.created_by_name}</td>
                                        <td>{formatDateTime(calibration.created_at)}</td>

                                        <td>
                                            {currentUser.permissions.equipment.calibration.update && <Button onClick={e => {
                                                setCurrentEditId(calibration.id);
                                                editModalControl.setOpen(true);
                                            }} color="blue" icon="edit" size="mini" />}
                                            {currentUser.permissions.equipment.calibration.delete && <Button onClick={e => {
                                                dialogControl.deletionConfirm('Calibration #' + calibration.id, agree => {
                                                    if (agree) {
                                                        return restRequest(`equipments/calibrations/${calibration.id}`, null, 'delete').then(() => {
                                                            setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
                                                        })
                                                    }

                                                    return Promise.resolve();
                                                });
                                            }} color="red" icon="delete" size="mini" />}
                                        </td>
                                    </tr>
                                })}
                            </Table.Body>
                        </Table>
                        <Page data={calibrations} updatePage={setSearchData}></Page>
                    </Segment>
                </Segment.Group>
            </Modal.Content>
        </Modal>

        <Editor equipmentId={equipmentId} id={currentEditId} modalControl={editModalControl} onFinish={() => {
            setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
            editModalControl.setOpen(false);
        }}></Editor>
    </>
}

function Editor(props: { modalControl: ModalControlData, equipmentId: number, id?: number, onFinish: () => void }) {
    const { equipmentId, id, modalControl, onFinish } = props;
    const isCreate = !id;

    const [data, setField, setData] = useFormData<EquipmentCalibrationData>({ equipment_id: equipmentId } as any);
    const [invalidError, setInvalidError] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [t, lang] = useContext(LanguageContext);

    useEffect(() => {
        setData({ equipment_id: equipmentId } as any);
        setInvalidError({});
        if (id) {
            setLoading(true);
            restRequest<EquipmentCalibrationData>('equipments/calibrations/' + id).then(setData).finally(() => setLoading(false))
        }
    }, [id]);

    useEffect(() => {
        setData(d => ({ ...d, equipment_id: equipmentId }));
    }, [equipmentId]);

    const onSubmit = (event: React.FormEvent<HTMLFormElement>, formProps: FormProps) => {
        event.preventDefault();

        setLoading(true);

        restRequest<EquipmentCalibrationData>(`equipments/calibrations/${isCreate ? '' : id}`, data, isCreate ? 'post' : 'put').then(result => {
            props.onFinish();
            if (isCreate) {
                setData({ equipment_id: equipmentId } as any);
            }
            setInvalidError({});
        }, resp => {
            if (resp.status === 2) {
                setInvalidError(resp.data);
            }
        }).finally(() => setLoading(false));
    }


    return <>
        <Modal {...modalControl.props} size="large">
            <Modal.Header>{t(isCreate ? 'Create' : 'Edit')} {t('Inventory')}#{equipmentId} -&gt; {t('Calibration')}{isCreate ? '' : ` #${id}`}</Modal.Header>
            <Modal.Content>
                <Form onSubmit={onSubmit} loading={loading}>
                    <Form.Group widths="equal">
                        <Form.Input label={t("Calibration Date")} error={fieldError('calibration_date', invalidError)}>
                            <ReactDatePicker
                                selected={data.calibration_date ? new Date(data.calibration_date) : null}
                                dateFormat="yyyy-MM-d" onChange={(d: Date) => setField(null, { name: 'calibration_date', value: d && d.toISOString() })}></ReactDatePicker>
                        </Form.Input>

                        <Form.Input label={t("Next Calibration")} error={fieldError('next_calibration_date', invalidError)}>
                            <ReactDatePicker
                                selected={data.next_calibration_date ? new Date(data.next_calibration_date) : null}
                                dateFormat="yyyy-MM-d" onChange={(d: Date) => setField(null, { name: 'next_calibration_date', value: d && d.toISOString() })}></ReactDatePicker>
                        </Form.Input>
                    </Form.Group>

                    <Form.Group widths="equal">
                        <Form.Input label={t("Reason For Missing Calibration")} error={fieldError('cause_of_no_calibration', invalidError)}>
                            <TextArea name="cause_of_no_calibration" value={data.cause_of_no_calibration || ''} onChange={setField} error={fieldError('cause_of_no_calibration', invalidError)} />
                        </Form.Input>
                    </Form.Group>

                    <Form.Group widths="equal">
                        <Form.Input label={t("Attachment")}>
                            <FilesInput name="files" value={data.files || []} onChange={setField} containerType={FileContainerType.EquipmentCalibrationFile} containerId={id || 0}></FilesInput>
                        </Form.Input>
                    </Form.Group>
                    <div>
                        <Button primary>{t('Submit')}</Button>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button as="a" onClick={e => modalControl.setOpen(false)}>{t('Cancel')}</Button>
                    </div>
                </Form>
            </Modal.Content>
        </Modal>
    </>
}